// Shared Google Maps loader. Every branch is time-bounded and failed attempts
// are evicted so a transient script/import failure can be retried.

import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

const DEFAULT_LIBRARIES = ['places', 'drawing', 'geometry']
const DEFAULT_TIMEOUT_MS = 12_000

let configuredApiKey = ''
let configuredLibraries = DEFAULT_LIBRARIES
let loaderConfigured = false
const libraryPromises = new Map()

function positiveNumber(value, fallback) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function mapsError(message, code) {
  const error = new Error(message)
  error.code = code
  error.retryable = true
  return error
}

function withTimeout(promise, timeoutMs, library) {
  let timer
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(
        () =>
          reject(
            mapsError(
              `Google Maps library "${library}" did not load in time.`,
              'google-maps-timeout'
            )
          ),
        timeoutMs
      )
    }),
  ]).finally(() => clearTimeout(timer))
}

function configureGoogleMaps(options = {}) {
  const apiKey = options.apiKey || configuredApiKey
  if (apiKey && apiKey !== configuredApiKey) {
    configuredApiKey = String(apiKey)
    loaderConfigured = false
    libraryPromises.clear()
  }
  if (Array.isArray(options.libraries) && options.libraries.length) {
    configuredLibraries = Array.from(new Set(options.libraries.filter(Boolean)))
  }
}

function ensureLoaderConfigured() {
  if (loaderConfigured || (typeof window !== 'undefined' && window.google?.maps)) return
  if (!configuredApiKey) {
    throw mapsError('Google Maps is unavailable because its API key is missing.', 'google-maps-key')
  }
  setOptions({ key: configuredApiKey, v: 'weekly' })
  loaderConfigured = true
}

function loadLibrary(library, timeoutMs) {
  if (libraryPromises.has(library)) return libraryPromises.get(library)

  const attempt = withTimeout(importLibrary(library), timeoutMs, library).catch(error => {
    libraryPromises.delete(library)
    throw error
  })
  libraryPromises.set(library, attempt)
  return attempt
}

export async function loadGoogleMaps(options = {}) {
  configureGoogleMaps(options)
  const timeoutMs = positiveNumber(
    options.timeoutMs || process.env.VUE_APP_GOOGLE_MAPS_LOAD_TIMEOUT_MS,
    DEFAULT_TIMEOUT_MS
  )
  const libraries =
    Array.isArray(options.libraries) && options.libraries.length
      ? Array.from(new Set(options.libraries.filter(Boolean)))
      : configuredLibraries

  ensureLoaderConfigured()
  await loadLibrary('maps', timeoutMs)
  await Promise.all(
    libraries.filter(library => library !== 'maps').map(library => loadLibrary(library, timeoutMs))
  )

  if (typeof window === 'undefined' || !window.google?.maps) {
    libraryPromises.clear()
    throw mapsError('Google Maps loaded without a usable browser API.', 'google-maps-unavailable')
  }
  return window.google
}

function retryingGoogleMapsThenable() {
  return {
    then(onFulfilled, onRejected) {
      return loadGoogleMaps().then(onFulfilled, onRejected)
    },
    catch(onRejected) {
      return loadGoogleMaps().catch(onRejected)
    },
    finally(onFinally) {
      return loadGoogleMaps().finally(onFinally)
    },
  }
}

export default {
  install: (app, options = {}) => {
    configureGoogleMaps(options)
    if (!app.config.globalProperties.$loadGoogleMaps) {
      app.config.globalProperties.$loadGoogleMaps = loadGoogleMaps
    }
    if (!app.config.globalProperties.$googleMaps) {
      app.config.globalProperties.$googleMaps = retryingGoogleMapsThenable()
    }
  },
}
